<script setup>
import { ref, onMounted } from "vue";
import { uploadImage } from "@/lib/uploads";
import { fetchLivestream, updateLivestream } from "./api";

const loading = ref(true), saving = ref(false), uploading = ref(false), success = ref(false), error = ref("");
const isLiveOn = ref(false);
const teaserUrl = ref("");
const fileInput = ref(null);

onMounted(async () => {
  loading.value = true;
  try {
    const data = await fetchLivestream();
    isLiveOn.value = data.live ?? false;
    teaserUrl.value = data.teaserImage ?? "";
  } catch { error.value = "Failed to load data."; }
  finally { loading.value = false; }
});

async function onFileSelected(e) {
  const file = e.target.files?.[0]; e.target.value = "";
  if (!file) return;
  uploading.value = true; error.value = "";
  try { teaserUrl.value = await uploadImage("livestream", file); }
  catch { error.value = "Couldn't upload the image."; }
  finally { uploading.value = false; }
}

async function saveChanges() {
  success.value = false; error.value = "";
  saving.value = true;
  try {
    await updateLivestream({ live: isLiveOn.value, teaserImage: teaserUrl.value });
    success.value = true;
  } catch { error.value = "Error saving changes."; }
  finally { saving.value = false; }
}
</script>

<template>
  <section>
    <h1 class="text-h5 mb-4">Manage Livestream</h1>
    <p v-if="loading">Loading…</p>
    <VCard v-else class="pa-4">
      <VAlert v-if="error" type="error" closable class="mb-4" @click:close="error = ''">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" closable class="mb-4" @click:close="success = false">Livestream settings updated successfully!</VAlert>
      <VRow>
        <VCol cols="12" md="6">
          <VSwitch v-model="isLiveOn" :label="`Livestream is: ${isLiveOn ? 'ON' : 'OFF'}`" color="success" inset />
          <div class="mt-4">
            <div class="text-body-2 font-weight-semibold mb-2">Teaser Image</div>
            <VRow>
              <VCol cols="12" sm="6">
                <VImg v-if="teaserUrl" :src="teaserUrl" max-height="200" contain class="bg-grey-lighten-2 rounded" />
                <div v-else class="bg-grey-lighten-2 rounded d-flex align-center justify-center" style="height:150px;width:100%">
                  <span class="text-disabled">No image selected</span>
                </div>
              </VCol>
              <VCol cols="12" sm="6" class="d-flex align-center">
                <input ref="fileInput" type="file" accept="image/x-png,image/jpeg,image/gif,image/webp" class="d-none" @change="onFileSelected" />
                <VBtn variant="outlined" :loading="uploading" @click="fileInput.click()">Browse Image</VBtn>
              </VCol>
            </VRow>
          </div>
        </VCol>

        <VCol cols="12" md="6">
          <div class="text-body-2 font-weight-semibold mb-2">Teaser Page Live Preview</div>
          <iframe src="https://aspials.com/campaign/livestreamteaser" frameborder="0" width="100%" height="500" />
          <div class="mt-2">
            <a href="https://app.maxi-cash.com/viewPlayer" target="_blank">Directly access the livestream page</a>
          </div>
        </VCol>

        <VCol cols="12">
          <VDivider class="my-4" />
          <VBtn color="primary" size="large" :loading="saving" :disabled="uploading" @click="saveChanges">Save Changes</VBtn>
        </VCol>
      </VRow>
    </VCard>
  </section>
</template>
